import { all, takeEvery, takeLatest, put, call } from 'redux-saga/effects';
import Api, { executeAction } from 'src/helpers/api';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { defaultApiVersionPath } from 'src/settings';
import { t } from 'src/i18n';
import notifications from 'src/redux/notifications/actions';
import type { RateEngineSide } from '../constants';
import { DownloadTemplateRequestAction, LoadEngineRequestAction, LoadVersionRequestAction } from './actions.d';
import actions from './actions';
import actionTypes from './actionTypes';

const engineApi = new Api('pricing/engines/:id');
const versionApi = new Api('pricing/engine_versions/:id');

// Built by hand: `Api`'s route templates interpolate one positional `:id`, this route has two segments of its own.
export const templateUrl = (versionId: string, side: RateEngineSide): string =>
  `${defaultApiVersionPath}/pricing/engine_versions/${versionId}/rate_cards/${side}/template`;

export function* loadEngineRequest({ payload }: LoadEngineRequestAction): Generator<any, void, any> {
  try {
    const result = yield call(sendRequest, engineApi.load, { id: payload.id }, notifications.REQUEST_ERROR);
    const engine = result?.data?.success?.data;

    if (result?.status === 200 && engine?.id) {
      yield put(actions.loadEngineSuccess({ engine }));

      return;
    }

    yield put(actions.loadEngineError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadEngineError());
  }
}

export function* loadVersionRequest({ payload }: LoadVersionRequestAction): Generator<any, void, any> {
  try {
    const result = yield call(sendRequest, versionApi.load, { id: payload.id }, notifications.REQUEST_ERROR);
    const version = result?.data?.success?.data;

    if (result?.status === 200 && version?.id) {
      yield put(actions.loadVersionSuccess({ version }));

      return;
    }

    yield put(actions.loadVersionError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadVersionError());
  }
}

interface IDownloadedFile {
  fileBlobUrl?: string;
  fileName?: string | null;
}

const HTTP_CONFLICT = 409;

// `sendRequest` logs the user out on any error message containing `401`, and the 409 here quotes admin-typed
// column names such as `401k Match`. Cost: an expired session during this one request does not log out.
const DOWNLOAD_REQUEST_OPTIONS = { ignoreLogout: true, preserveErrorStatus: true };

const downloadRefusal = (result: { status?: number; message?: string } | undefined): string => {
  // Under `preserveErrorStatus` only a 403 keeps its own sentence, and it is worth preferring: it
  // separates "the feature is off for this organisation" from "you may not do this".
  if (result?.message) return result.message;

  if (result?.status === HTTP_CONFLICT) return t('dash.admin.rateEngine.notifications.template_columns_not_ready');

  return t('dash.admin.rateEngine.notifications.template_download_failed');
};

// `helpers/request` replaces a `text/csv` body with `{ fileBlobUrl, fileName }` and loses the status
// doing so — the blob's presence, not a 200, is what says the request succeeded.
export function* downloadTemplateRequest({ payload }: DownloadTemplateRequestAction): Generator<any, void, any> {
  try {
    const result = yield call(
      sendRequest,
      executeAction,
      { url: templateUrl(payload.versionId, payload.side), method: 'GET' },
      undefined,
      undefined,
      undefined,
      DOWNLOAD_REQUEST_OPTIONS,
    );
    const { fileBlobUrl, fileName }: IDownloadedFile = result?.data ?? {};

    if (!fileBlobUrl) {
      logger.error(
        new Error(`Rate card template carried no file (version=${payload.versionId}, side=${payload.side})`),
      );
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: downloadRefusal(result),
        }),
      );

      return;
    }

    const link = document.createElement('a');

    link.href = fileBlobUrl;
    // `Content-Disposition` is not CORS-exposed, so `fileName` is usually absent; without a fallback
    // the browser names the download after the blob URL — a bare UUID with no extension.
    link.download = fileName || `${payload.side}_rate_card_template.csv`;
    link.click();
    // Not revoked: the click downloads asynchronously, and releasing the blob can cancel the transfer.
  } catch (error) {
    logger.error(error);
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
        message: t('dash.admin.rateEngine.notifications.template_download_failed'),
      }),
    );
  } finally {
    yield put(actions.downloadTemplateSettled(payload));
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.LOAD_ENGINE_REQUEST, loadEngineRequest),
    takeLatest(actionTypes.LOAD_VERSION_REQUEST, loadVersionRequest),
    // `takeEvery`, not `takeLatest`: a cancelled task still runs its `finally`, so a second request would
    // settle the first one's pending slot mid-flight and the first file would never be handed over.
    takeEvery(actionTypes.DOWNLOAD_TEMPLATE_REQUEST, downloadTemplateRequest),
  ]);
}
